import { throw_expression } from "./utils/flow-control";
import { QuadTree } from "@game.object/ts-game-toolbox/src/trees/QuadTree";
import { Rect, RectI } from "@game.object/ts-game-toolbox/src/geometries/Rect";
import { Vector2, Vector2I } from "@game.object/ts-game-toolbox/src/geometries/Vector2";

const INITIAL_PHEROMON_DROP_INTENSITY = 10;

export class Game {
    public canvas: HTMLCanvasElement;
    public context: CanvasRenderingContext2D;
    public ants: Array<Ant> = [];
    public pheromones: Array<Pheromon> = [];
    public pheromone_tree: QuadTree<Pheromon> = new QuadTree(new Rect(0, 0, 800, 600));
    public foods: Array<Food> = [];
    public food_tree: QuadTree<Food> = new QuadTree(new Rect(0, 0, 800, 600));
    // controls
    public controls_form: HTMLFormElement;
    public controls_show_ants: HTMLInputElement;
    public controls_show_foods: HTMLInputElement;
    public controls_show_pheromones: HTMLInputElement;
    public controls_simulation_speed: HTMLInputElement;
    public controls_simulation_steps: HTMLInputElement;

    constructor() {
        const canvas = document.getElementById('app') ?? throw_expression("Canvas not found");
        if (!(canvas instanceof HTMLCanvasElement)) throw new Error("Canvas not found");
        this.canvas = canvas;
        this.context = canvas.getContext("2d") ?? throw_expression("Context not found");
        this.canvas.addEventListener("click", (event: MouseEvent) => {
            const x = (event.clientX - this.canvas.offsetLeft) / this.canvas.clientWidth * this.canvas.width;
            const y = (event.clientY - this.canvas.offsetTop) / this.canvas.clientHeight * this.canvas.height;
            this.on_click(x, y);
        });
        // controls
        const controls = document.getElementById('controls');
        if (!(controls instanceof HTMLFormElement)) throw new Error("Controls not found");
        this.controls_form = controls;
        const show_ants = controls.querySelector("[name=show-ants]");
        if (!(show_ants instanceof HTMLInputElement)) throw new Error("Controls not found");
        this.controls_show_ants = show_ants;
        const show_food = controls.querySelector("[name=show-foods]");
        if (!(show_food instanceof HTMLInputElement)) throw new Error("Controls not found");
        this.controls_show_foods = show_food;
        const show_pheromones = controls.querySelector("[name=show-pheromones]");
        if (!(show_pheromones instanceof HTMLInputElement)) throw new Error("Controls not found");
        this.controls_show_pheromones = show_pheromones;

        const simulation_speed = controls.querySelector("[name=simulation-speed]");
        if (!(simulation_speed instanceof HTMLInputElement)) throw new Error("Controls not found");
        this.controls_simulation_speed = simulation_speed;
        const simulation_steps = controls.querySelector("[name=simulation-steps]");
        if (!(simulation_steps instanceof HTMLInputElement)) throw new Error("Controls not found");
        this.controls_simulation_steps = simulation_steps;
    }

    public init() {
        this.ants = [];
        const ant_count = 150;
        for (let i = 0; i < ant_count; ++i) {
            const ant = new Ant(this);
            const x = 400 + Math.random() * 100;
            const y = 300 + Math.random() * 100;
            ant.x = x;
            ant.y = y;
            this.ants.push(ant);
        }
        this.foods = [];
        const food_regions = 15;
        const food_count = 150;
        for (let i = 0; i < food_regions; ++i) {
            const rx = Math.random() * 700 + 50;
            const ry = Math.random() * 300 + 50;
            for (let j = 0; j < food_count; ++j) {
                const x = rx + Math.random() * 50;
                const y = ry + Math.random() * 50;
                this.add_food(x, y);
            }
        }
    }

    public on_click(x: number, y: number) {
        const food_count = 150;
        const rx = x;
        const ry = y;
        for (let j = 0; j < food_count; ++j) {
            const x = rx + Math.random() * 50;
            const y = ry + Math.random() * 50;
            this.add_food(x, y);
        }
    }

    public run() {
        this.init();
        const loop = () => {
            for (let i = 0; i < parseInt(this.controls_simulation_speed.value); i++) {
                this.update(parseInt(this.controls_simulation_steps.value) / 60);
            }
            requestAnimationFrame(loop);
        }
        requestAnimationFrame(loop);
    }

    public update(delta_seconds: number) {
        this.draw();
        this.ants.forEach((ant: Ant) => {
            ant.update(delta_seconds);
        })
        this.pheromones = this.pheromones.filter((pheromon) => {
            pheromon.update(delta_seconds);
            if (pheromon.intensity > 0) return true;
            this.pheromone_tree.remove(pheromon);
            return false;
        });
    }

    public draw() {
        this.context.clearRect(0, 0, 800, 600);

        if (this.controls_show_pheromones.checked) {

            this.pheromones.forEach((pheromon) => {
                const intensity_alpha = Math.min(0xff, Math.max(0, Math.floor((pheromon.intensity) * 0xff / 20)));
                this.context.fillStyle = (pheromon.type
                    ? "#8800ff"
                    : "#0088ff")
                    + ("00" + intensity_alpha.toString(16)).substr(-2, 2);
                this.context.fillRect(pheromon.x - 1, pheromon.y - 1, 3, 3);
            });
        }

        if (this.controls_show_ants.checked) {
            this.context.beginPath();
            this.context.strokeStyle = "red";
            this.ants.forEach((ant) => {
                this.context.moveTo(ant.x + 5, ant.y);
                this.context.ellipse(ant.x, ant.y, 5, 5, 0, 0, Math.PI * 2);
            });
            this.context.stroke();
        }

        if (this.controls_show_foods.checked) {
            this.context.beginPath();
            this.context.fillStyle = "green";
            this.foods.forEach((food) => {
                this.context.moveTo(food.x + 4, food.y);
                this.context.ellipse(food.x, food.y, 4, 4, 0, 0, Math.PI * 2);
            });
            this.context.fill();
        }
    }

    public add_food(x: number, y: number) {
        const food = new Food();
        food.x = x - food.h / 2;
        food.y = y - food.w / 2;
        this.foods.push(food);
        this.food_tree.add(food);
    }

    public drop_pheromone(pos: Vector2I, type: PheromonType, strength: number) {
        const phe = new Pheromon();
        phe.x = pos.x;
        phe.y = pos.y;
        phe.type = type;
        phe.intensity = strength;
        this.pheromones.push(phe);
        this.pheromone_tree.add(phe);
    }
}

function rect_with_center(center: Vector2I, width: number, height: number): Rect {
    return new Rect(center.x - width / 2, center.y - height / 2, width, height);
}

class Ant {
    public x: number = 400;
    public y: number = 300;
    public vx: number = 0;
    public vy: number = 0;
    public transport: Food | null = null;
    public cooldown: number = 0;
    public time_until_random: number = 0;
    public pheromone_drop_intensity: number = 0;

    constructor(public game: Game) {

    }

    public update(delta_seconds: number) {
        this.x += this.vx * delta_seconds * 25;
        this.y += this.vy * delta_seconds * 25;
        const dx = this.x - 400;
        const dy = this.y - 300;
        const d2 = dx * dx + dy * dy;
        if (d2 < 2500) {
            this.pheromone_drop_intensity = INITIAL_PHEROMON_DROP_INTENSITY;
            if (this.transport !== null) {
                this.game.foods = this.game.foods.filter((f) => f !== this.transport);
                this.transport = null;
                this.vx *= -1;
                this.vy *= -1;
            }
        }

        // add aimed movement
        const offset_rotation = Math.PI / 4;
        const offset_distance = 20;
        const sense_radius = 10;


        const in_range_template_func = (center: Vector2) => {
            return (pheromon: Pheromon) => {
                return center.cpy().sub(pheromon).len2() < sense_radius * sense_radius;
            }
        };


        const rotation = Math.atan2(this.vy, this.vx);
        const left_sense_offset = (Vector2.from_angle(rotation - offset_rotation, offset_distance));
        const forward_sense_offset = (Vector2.from_angle(rotation, offset_distance));
        const right_sense_offset = (Vector2.from_angle(rotation + offset_rotation, offset_distance));
        const left_sense_center = (left_sense_offset.cpy()).add(this);
        const forward_sense_center = (forward_sense_offset.cpy()).add(this);
        const right_sense_center = (right_sense_offset.cpy()).add(this);
        const left_sense = this.game.pheromone_tree.pick(rect_with_center(left_sense_center, sense_radius, sense_radius))
            .filter(in_range_template_func(left_sense_center));
        const forward_sense = this.game.pheromone_tree.pick(rect_with_center(forward_sense_center, sense_radius, sense_radius))
            .filter(in_range_template_func(forward_sense_center));
        const right_sense = this.game.pheromone_tree.pick(rect_with_center(right_sense_center, sense_radius, sense_radius))
            .filter(in_range_template_func(right_sense_center));
        const left_sense_value = this.evaluate_pheromons(left_sense);
        const forward_sense_value = this.evaluate_pheromons(forward_sense);
        const right_sense_value = this.evaluate_pheromons(right_sense);

        this.vx += (Math.random() - 0.5 - Math.abs(dx) * dx / (1200 * 1200)) / 25;
        this.vy += (Math.random() - 0.5 - Math.abs(dy) * dy / (1200 * 1200)) / 25;
        if (forward_sense_value > right_sense_value && forward_sense_value > left_sense_value) {
            this.vx += forward_sense_offset.x / 10;
            this.vy += forward_sense_offset.y / 10;
            this.time_until_random = 1;
        }
        if (left_sense_value > forward_sense_value && left_sense_value > right_sense_value) {
            this.vx += left_sense_offset.x / 10;
            this.vy += left_sense_offset.y / 10;
            this.time_until_random = 1;
        }
        if (right_sense_value > left_sense_value && right_sense_value > forward_sense_value) {
            this.vx += right_sense_offset.x / 10;
            this.vy += right_sense_offset.y / 10;
            this.time_until_random = 1;
        }
        if (right_sense_value === left_sense_value && left_sense_value === forward_sense_value) {
            // add random movement
            // this.vx += 1;
            this.time_until_random -= delta_seconds;
            if (this.time_until_random < 0) {
                this.vx += (Math.random() - 0.5 - Math.abs(dx) * dx / (1200 * 1200)) / 5;
                this.vy += (Math.random() - 0.5 - Math.abs(dy) * dy / (1200 * 1200)) / 5;
            }
        }

        const vlen = Math.sqrt(this.vx * this.vx + this.vy * this.vy);
        this.vx /= vlen;
        this.vy /= vlen;

        if (this.transport !== null) {
            this.update_transport_food(delta_seconds);
        } else {
            this.update_pickup_food(delta_seconds);
        }
        this.cooldown -= delta_seconds;
        this.pheromone_drop_intensity *= 0.995;
        if (this.cooldown < 0) {
            this.cooldown = 0.5;
            this.game.drop_pheromone({
                x: this.x,
                y: this.y,
            },
                this.transport === null ? PheromonType.HOME : PheromonType.FOOD,
                this.pheromone_drop_intensity * 2,
            )
        }
    }

    public evaluate_pheromons(pheromons: Array<Pheromon>): number {
        return pheromons.reduce((sum, phe) => {
            switch (phe.type) {
                case PheromonType.FOOD:
                    return sum + (this.transport === null ? phe.intensity : 0);
                case PheromonType.HOME:
                    return sum + (this.transport === null ? 0 : phe.intensity);
            }
        }, 0);
    }

    public move_random() {

    }

    public update_transport_food(delta_seconds: number) {
        if (!this.transport) return;
        this.transport.x = this.x;
        this.transport.y = this.y;
    }

    public update_pickup_food(delta_seconds: number) {
        const found_food = this.game.food_tree.pick({
            x: this.x,
            y: this.y,
            w: 8,
            h: 8,
        }).filter((food) => {
            return food.transported_by === null;
        });
        if (found_food.length > 0) {
            this.transport = found_food[0];
            found_food[0].transported_by = this;
            this.game.food_tree.remove(found_food[0]);
            this.vx *= -1;
            this.vy *= -1;
            this.pheromone_drop_intensity = INITIAL_PHEROMON_DROP_INTENSITY;
        }
    }
}

class Food implements RectI {
    public w: number = 5;
    public h: number = 5;
    public x: number = 0;
    public y: number = 0;
    public transported_by: Ant | null = null;
}

class Pheromon implements RectI {
    public w: number = 4;
    public h: number = 4;
    public x: number = 0;
    public y: number = 0;
    public type: PheromonType = PheromonType.HOME;
    public intensity: number = 20;

    public update(delta_seconds: number) {
        this.intensity -= delta_seconds;
    }
}

enum PheromonType {
    HOME, FOOD
}